import { css } from '@emotion/react';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { DateRange, isSameDate, BOUND_SIDE } from 'shared';

import { DeviceType, useMaxDeviceWidth } from '../../hooks/useDeviceType';
import { DatePicker, DayInfo } from '../datePicker';
import { theme } from '../theme';

const styles = {
  datePicker: css`
    width: 540px;

    @media (max-width: ${theme.breakpoints.tablet}) {
      width: 100%;
    }
  `,
};

export type FlightCalenderProps = {
  outboundDate: DateTime<boolean> | undefined;
  inboundDate: DateTime<boolean> | undefined;
  handleDateChange: (boundSide: BOUND_SIDE, date: DateTime) => void;
  handleDisplayedMonthChange?: (dates: {
    outboundDate: DateTime | undefined;
    inboundDate: DateTime | undefined;
  }) => void;
  selectable: DateRange;
  outboundHighlighted?: DateTime[];
  inboundHighlighted?: DateTime[];
  displayOutboundDatePicker?: boolean;
  displayInboundDatePicker?: boolean;
  outboundDayInfo?: DayInfo;
  inboundDayInfo?: DayInfo;
};

export function FlightCalender(props: FlightCalenderProps) {
  const isSmall = useMaxDeviceWidth(DeviceType.Tablet);

  const [displayedMonths, setDisplayedMonths] = useState({
    outboundDate: props.outboundDate,
    inboundDate: props.inboundDate,
  });

  const handleDisplayedMonthChange = (boundSide: BOUND_SIDE) => (date: DateTime) => {
    const outboundDate = boundSide === BOUND_SIDE.Outbound ? date : displayedMonths.outboundDate;
    const inboundDate = boundSide === BOUND_SIDE.Inbound ? date : displayedMonths.inboundDate;

    const isInvalidBoundOrder = outboundDate && inboundDate && inboundDate.diff(outboundDate, 'months').months < 0;

    const value = {
      outboundDate: isInvalidBoundOrder ? date : outboundDate,
      inboundDate: isInvalidBoundOrder ? date : inboundDate,
    };

    if (
      !isSameDate(displayedMonths.inboundDate, value.inboundDate) ||
      !isSameDate(displayedMonths.outboundDate, value.outboundDate)
    ) {
      setDisplayedMonths(value);
      props.handleDisplayedMonthChange?.(value);
    }
  };

  return (
    <>
      {props.displayOutboundDatePicker && (
        <DatePicker
          css={styles.datePicker}
          selectable={props.selectable}
          highlighted={props.outboundHighlighted}
          showDateInput={!isSmall}
          title={t('common:outBound')}
          selected={{
            primary: props.outboundDate,
            secondary: props.inboundDate,
            emphasized: props.displayInboundDatePicker ? 'primary' : 'both',
          }}
          handleSelect={date => props.handleDateChange(BOUND_SIDE.Outbound, date)}
          handleDisplayedMonthChange={handleDisplayedMonthChange(BOUND_SIDE.Outbound)}
          dayInfo={props.outboundDayInfo}
          displayedDate={displayedMonths.outboundDate}
        />
      )}

      {props.displayInboundDatePicker && (
        <DatePicker
          css={styles.datePicker}
          selectable={props.selectable}
          highlighted={props.inboundHighlighted}
          showDateInput={!isSmall}
          title={t('common:inBound')}
          selected={{
            primary: props.inboundDate,
            secondary: props.outboundDate,
            emphasized: props.displayOutboundDatePicker ? 'primary' : 'both',
          }}
          handleSelect={date => props.handleDateChange(BOUND_SIDE.Inbound, date)}
          handleDisplayedMonthChange={handleDisplayedMonthChange(BOUND_SIDE.Inbound)}
          dayInfo={props.inboundDayInfo}
          displayedDate={displayedMonths.inboundDate}
        />
      )}
    </>
  );
}
